/** `core.vitals` — lives with a hurt pose, hidden respawn delay and an
 * invulnerability window; `rule.contactDamage` — touch-to-hurt between tagged
 * entities, with exempt attacker tags, an opening-safety window and, for
 * projectiles, sparing the seat that fired them. Damage is a `damage` signal
 * (any module may send one); vitals is its one consumer, and a blow that
 * costs a life is reported as a `hurt` signal {x, y, slot, lives, by: the
 * damage signal's source id, or null} for the rules that score it.
 * Likewise vitals owns the respawn point: a `checkpoint` signal {x, y} moves
 * it, and a `park` signal {ticks?} takes the entity off the field and
 * respawns it there `ticks` ticks later (counting the signal's own tick)
 * without a life lost; a `restore` signal refills the lives of an entity not
 * yet out (a new round of a versus match). */
import { bool, id, int, list, obj } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { addTag, overlaps, removeTag } from '../world.js';
import { shooterSlot } from './misc.js';

export type VitalsConfig = { lives: number; hurtTicks: number; respawnTicks: number; invulnTicks: number };

export const vitals = defineModule<VitalsConfig>({
  id: 'core.vitals', version: 1, attach: 'entity', role: 'rule',
  summary: 'Lives: on damage show a hurt pose, hide for a respawn delay, reappear at the spawn point with invulnerability; out when lives reach zero. A park signal hides then respawns without a life lost; a checkpoint signal moves the spawn point; a forfeit signal costs a life without a pose; a restore signal refills the lives.',
  config: obj({ lives: int(1, 9), hurtTicks: int(0, 120), respawnTicks: int(0, 300), invulnTicks: int(0, 600) }),
  requires: [], events: ['hurt', 'down', 'respawn'],
  presentation: { hurt: { cue: 'hurt', vfx: { effect: 'burst', palette: 'slot', count: 10 } }, down: { cue: 'fail', vfx: { effect: 'shake' } }, respawn: { cue: 'none' } },
  proofs: ['damage-costs-one-life', 'invulnerable-window-ignores-damage', 'respawns-at-spawn', 'out-at-zero', 'flicker-while-invulnerable',
    'park-respawns-without-cost', 'forfeit-costs-a-life', 'checkpoint-moves-respawn', 'signals-hurt-with-its-source', 'restore-refills-lives'],
  includeHidden: true,
  animStates: () => ['hit', 'down'],
  hud: () => [{ id: 'lives', label: 'LIVES', kind: 'pips' }],
  hudValues(ctx) {
    const lives: number[] = [];
    for (const e of ctx.entities({ status: 'any' })) {
      const d = ctx.peer(e, 'core.vitals');
      if (d && e.slot !== null) lives[e.slot] = read(d, 'lives');
    }
    return { lives: Array.from(lives, v => v ?? 0) };
  },
  init(ctx, e) {
    Object.assign(ctx.data(e), { lives: ctx.config.lives, phase: 'alive', timer: 0, invuln: 0, spawnX: e.x, spawnY: e.y, facing: e.facing });
  },
  systems: {
    rules(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), c = ctx.config;
      if (d.phase !== 'out' && ctx.signals('restore').some(s => s.target === e.id)) d.lives = c.lives;
      // A forfeited life (lost elsewhere — a captive carried away) costs a
      // life without a hurt pose; at zero it ends like any other.
      if (d.phase !== 'out' && ctx.signals('forfeit').some(s => s.target === e.id)) {
        d.lives = read(d, 'lives') - 1;
        ctx.emit('hurt', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0, lives: read(d, 'lives'), forfeit: true });
        if (read(d, 'lives') <= 0) { d.phase = 'hurt'; d.timer = e.status === 'hidden' ? 1 : c.hurtTicks; if (e.status !== 'hidden') e.status = 'frozen'; }
      }
      if (d.phase === 'alive' && read(d, 'invuln') === 0) {
        const hit = ctx.signals('damage').find(s => s.target === e.id);
        if (hit) {
          d.lives = read(d, 'lives') - 1;
          d.phase = 'hurt'; d.timer = c.hurtTicks;
          e.status = 'frozen'; e.vx = 0; e.vy = 0;
          ctx.emit('hurt', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0, lives: read(d, 'lives') });
          ctx.signal('hurt', e, null, { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? -1, lives: read(d, 'lives'), by: hit.source, tags: [...e.tags] });
        }
      }
      // Parked (taken off the field without a life lost — captured, or warped
      // to a new entrance): hidden, then the normal respawn at the spawn point
      // on the `ticks`-th tick counting this one.
      const park = d.phase === 'alive' ? ctx.signals('park').find(s => s.target === e.id) : undefined;
      if (park) {
        d.phase = 'respawning'; d.timer = typeof park.data.ticks === 'number' ? park.data.ticks : c.respawnTicks;
        e.status = 'hidden'; e.vx = 0; e.vy = 0;
      }
      // A checkpoint moves the respawn point (before a respawn this tick uses it).
      const checkpoint = ctx.signals('checkpoint').find(s => s.target === e.id);
      if (checkpoint && typeof checkpoint.data.x === 'number' && typeof checkpoint.data.y === 'number') {
        d.spawnX = checkpoint.data.x; d.spawnY = checkpoint.data.y;
      }
      const respawn = () => {
        d.phase = 'alive'; d.invuln = c.invulnTicks;
        e.x = read(d, 'spawnX'); e.y = read(d, 'spawnY'); e.vx = 0; e.vy = 0; e.facing = read(d, 'facing') === -1 ? -1 : 1; e.angle = 0;
        e.status = 'active'; e.grounded = false; e.groundId = null;
        ctx.emit('respawn', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      };
      if (d.phase === 'hurt') {
        d.timer = read(d, 'timer') - 1;
        if (read(d, 'timer') <= 0) {
          if (read(d, 'lives') <= 0) {
            d.phase = 'out'; e.status = 'hidden'; addTag(e, 'out');
            ctx.emit('down', { x: e.x, y: e.y, slot: e.slot ?? 0 });
          } else if (c.respawnTicks === 0) respawn(); // no respawn delay: never leaves the field
          else { d.phase = 'respawning'; d.timer = c.respawnTicks; e.status = 'hidden'; }
        }
      } else if (d.phase === 'respawning') {
        d.timer = read(d, 'timer') - 1;
        if (read(d, 'timer') <= 0) respawn();
      } else if (d.phase === 'alive' && read(d, 'invuln') > 0) d.invuln = read(d, 'invuln') - 1;
      if (read(d, 'invuln') > 0 || d.phase !== 'alive') addTag(e, 'invulnerable'); else removeTag(e, 'invulnerable');
    },
    present(ctx, e) {
      if (!e || e.status === 'hidden') return;
      const d = ctx.data(e);
      if (d.phase === 'hurt') ctx.setAnim(e, read(d, 'lives') > 0 ? 'hit' : 'down');
      else if (read(d, 'invuln') > 0) ctx.react(e, 'flicker');
    },
  },
});

export type ContactDamageConfig = {
  attackerTag: string; victimTag: string; exceptAttackerTags: string[];
  /** Ticks after a phase starts during which contact is harmless. */
  openingSafeTicks: number;
  /** Projectiles: the attacker is spent on contact. */
  consumeAttacker: boolean;
  /** Projectiles: an attacker never hurts the seat that fired it (its own
   * seat, or the seat whose weapon lists it as a live shot — `shooterSlot`).
   * Absent: any victim it touches. */
  spareShooter?: boolean;
};

export const contactDamage = defineModule<ContactDamageConfig>({
  id: 'rule.contactDamage', version: 1, attach: 'global', role: 'rule',
  summary: 'Touching an attacker-tagged entity damages a victim-tagged one (exempt tags, opening safety, optional consumed attacker, optionally sparing the seat that fired it).',
  config: obj({ attackerTag: id(), victimTag: id(), exceptAttackerTags: list(id(), 8), openingSafeTicks: int(0, 600), consumeAttacker: bool(), spareShooter: bool() },
    ['spareShooter']),
  requires: ['core.vitals'], events: [], presentation: {},
  proofs: ['touch-damages', 'exempt-tags-harmless', 'opening-safety', 'invulnerable-victim-skipped', 'consumed-attacker', 'spares-its-shooter'],
  animStates: () => [],
  systems: {
    interact(ctx) {
      const c = ctx.config;
      if (ctx.scalar('phase.tick', c.openingSafeTicks) < c.openingSafeTicks) return;
      const attackers = ctx.entities({ tag: c.attackerTag, status: 'active' })
        .filter(a => !c.exceptAttackerTags.some(t => a.tags.includes(t)));
      for (const victim of ctx.entities({ tag: c.victimTag, status: 'active' })) {
        if (victim.tags.includes('invulnerable')) continue;
        const attacker = attackers.find(a => a.id !== victim.id && ctx.world.entities[a.id] && overlaps(ctx.world, victim, a, 0.08)
          && !(c.spareShooter === true && victim.slot !== null && shooterSlot(ctx, a.id) === victim.slot));
        if (!attacker) continue;
        ctx.signal('damage', victim, attacker, { amount: 1, x: victim.x, y: victim.y - victim.h / 2, tags: [...victim.tags] });
        if (c.consumeAttacker) ctx.despawn(attacker, 'spent');
      }
    },
  },
});
